<template>
  <div
    class="h-full overflow-auto w-60 flex flex-col bg-white dark:bg-slate-900 border-r dark:border-slate-700 rtl:border-r-0 rtl:border-l border-slate-50 text-sm"
  >
    <sidebar-header
      :thumbnail-src="thumbnailSrc"
      :header-title="headerTitle"
      :sub-title="subTitle"
      :portal-link="portalLink"
      class="px-4"
      @open-popover="openPortalPopover"
    />
    <transition-group
      name="menu-list"
      tag="ul"
      class="py-2 px-4 list-none ml-0 mb-0"
    >
      <secondary-nav-item
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
      />
      <drag-zone
        :categories="sortedCategories"
        key="drag-zone"
        @reorder="handleReorder"
      >
        <div class="flex justify-between">
          <span
            class="px-2 pt-1 my-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
          >
            {{ $t(`SIDEBAR.HELP_CENTER.CATEGORY`) }}
          </span>
          <div class="flex items-center">
            <woot-button
              size="tiny"
              variant="clear"
              color-scheme="secondary"
              icon="add"
              class="p-0 ml-2"
              @click="onClickOpenAddCatogoryModal"
            />
          </div>
        </div>

        <transition-group name="menu-list" tag="div">
          <category-item
            v-for="category in formattedCategories"
            :key="category.id"
            :category="category"
            @show-context-menu="handleContextMenu"
          />
        </transition-group>
      </drag-zone>
      <p
        v-if="!hasCategory"
        key="empty-category-nessage"
        class="p-1.5 px-4 text-slate-300"
      >
        {{ $t('SIDEBAR.HELP_CENTER.CATEGORY_EMPTY_MESSAGE') }}
      </p>
    </transition-group>

    <category-context-menu
      :is-visible="contextMenu.isVisible"
      :position="contextMenu.position"
      :is-first-item="getFirstCategory"
      @edit="handleEdit"
      @move-top="handleMoveTop"
      @delete="handleDelete"
      @close="closeContextMenu"
    />

    <edit-category
      v-if="showEditCategoryModal"
      :show.sync="showEditCategoryModal"
      :portal-name="currentPortalName"
      :locale="selectedCategory.locale"
      :category="selectedCategory"
      :selected-portal-slug="portalSlug"
      @cancel="closeEditCategoryModal"
    />
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import SecondaryNavItem from 'dashboard/components/layout/sidebarComponents/SecondaryNavItem.vue';
import SidebarHeader from './SidebarHeader.vue';
import CategoryItem from './CategoryItem.vue';
import DragZone from './DragZone.vue';
import CategoryContextMenu from './CategoryContextMenu.vue';
import EditCategory from '../../pages/categories/EditCategory.vue';

export default {
  components: {
    SecondaryNavItem,
    SidebarHeader,
    CategoryItem,
    DragZone,
    CategoryContextMenu,
    EditCategory
  },
  mixins: [alertMixin],
  props: {
    thumbnailSrc: {
      type: String,
      default: '',
    },
    headerTitle: {
      type: String,
      default: '',
    },
    subTitle: {
      type: String,
      default: '',
    },
    portalSlug: {
      type: String,
      default: '',
    },
    localeSlug: {
      type: String,
      default: '',
    },
    accessibleMenuItems: {
      type: Array,
      default: () => [],
    },
    additionalSecondaryMenuItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showEditCategoryModal: false,
      selectedCategory: {},
      contextMenu: {
        isVisible: false,
        position: { x: 0, y: 0 },
        selectedCategory: null
      }
    };
  },
  computed: {
    sortedCategories() {
      return (this.additionalSecondaryMenuItems[0]?.children || [])
        .sort((a, b) => a.position - b.position);
    },
    formattedCategories() {
      return this.sortedCategories.map(category => {
        const nameWithIcon = category.label;
        const iconMatch = nameWithIcon.match(/^(.*?)\s/);
        const icon = iconMatch ? iconMatch[1] : '';
        const name = nameWithIcon.replace(/^.*?\s/, '');
        
        return {
          id: category.id,
          name: name,
          icon: icon,
          count: category.count,
          toState: category.toState,
        };
      });
    },
    hasCategory() {
      return (
        this.additionalSecondaryMenuItems[0] &&
        this.additionalSecondaryMenuItems[0].children.length > 0
      );
    },
    portalLink() {
      return `/hc/${this.portalSlug}/${this.localeSlug}`;
    },
    currentPortalName() {
      return this.portalName || this.$store.getters['portals/getSelectedPortal']?.name;
    },
    getFirstCategory() {
      return this.sortedCategories[0]?.id === this.contextMenu.selectedCategory?.id;
    },
  },
  methods: {
    async handleReorder(data) {
      if (data.type === 'category') {
        try {
          await this.$store.dispatch('categories/updatePosition', {
            portalSlug: this.portalSlug,
            categoryId: data.id,
            position: data.newPosition,
            locale: this.localeSlug
          });
          this.showAlert(this.$t('HELP_CENTER.CATEGORY.REORDER.SUCCESS'));
        } catch (error) {
          this.showAlert(this.$t('HELP_CENTER.CATEGORY.REORDER.ERROR'));
        }
      }
    },
    onSearch(value) {
      this.$emit('input', value);
    },
    openPortalPopover() {
      this.$emit('open-popover');
    },
    onClickOpenAddCatogoryModal() {
      this.$emit('open-modal');
    },
    handleContextMenu({ event, category }) {
      this.contextMenu = {
        isVisible: true,
        position: { x: event.clientX, y: event.clientY },
        selectedCategory: category
      };
    },
    closeContextMenu() {
      this.contextMenu.isVisible = false;
    },
    handleEdit() {
      const menuCategory = this.contextMenu.selectedCategory;
      this.selectedCategory = {
        id: menuCategory.id,
        name: menuCategory.name,
        slug: menuCategory.slug || menuCategory.name.toLowerCase().replace(/\s+/g, '-'),
        locale: this.localeSlug,
        description: menuCategory.description || '',
        position: menuCategory.position,
        icon: menuCategory.icon,
        portal_id: this.portalSlug,
        meta: {
          articles_count: menuCategory.count
        }
      };
      this.showEditCategoryModal = true;
      this.closeContextMenu();
    },
    closeEditCategoryModal() {
      this.showEditCategoryModal = false;
      this.selectedCategory = {};
    },
    handleMoveTop() {
      const { selectedCategory } = this.contextMenu;
      this.$store.dispatch('categories/updatePosition', {
        portalSlug: this.portalSlug,
        categoryId: selectedCategory.id,
        position: 1,
        locale: this.localeSlug
      });
      this.closeContextMenu();
    },
    async handleDelete() {
      const { selectedCategory } = this.contextMenu;
      await this.$store.dispatch('categories/delete', {
        portalSlug: this.portalSlug,
        categoryId: selectedCategory.id
      });
      this.closeContextMenu();
    }
  },
};
</script>
